import React, { ReactNode, useCallback, useState } from 'react';
import Modal from '../Modal';
import './confirm-dialog.scss';

/**
 * An in-app replacement for window.confirm / window.alert.
 *
 * The browser dialogs are unstyled, cannot be themed, are positioned by the
 * browser rather than the page, and on some platforms are suppressed entirely
 * when they fire outside a user gesture -- which for an async flow (the PDF
 * export pausing halfway to ask about missing images) means the question is
 * never asked and the answer defaults to "no".
 *
 * The hook is promise-based so an async function can `await` an answer in
 * place, exactly where window.confirm used to sit.
 */

export type ConfirmTone = 'info' | 'warning' | 'danger';

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  /** Omit for a notice: a single acknowledging button and no cancel. */
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

interface PendingRequest extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

const TONE_ICON: Record<ConfirmTone, ReactNode> = {
  info: <path d="M12 16v-4M12 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />,
  warning: <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />,
  danger: <path d="M12 9v4M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />,
};

export const ConfirmDialog: React.FC<{
  request: PendingRequest | null;
  onResolve: (confirmed: boolean) => void;
}> = ({ request, onResolve }) => {
  if (!request) return null;

  const tone: ConfirmTone = request.tone ?? 'warning';
  const isNotice = !request.confirmLabel;

  return (
    <Modal
      isOpen
      onClose={() => onResolve(false)}
      title={request.title}
      size="sm"
      hasFooter
      className={`confirm-dialog confirm-dialog--${tone}`}
      // The document editor is a full-screen overlay; without this the dialog
      // renders behind it and the export appears to hang.
      overlayClassName="modal-overlay--above-fullscreen"
      footerContent={
        <div className="confirm-dialog__actions">
          {!isNotice && (
            <button type="button" className="btn-secondary" onClick={() => onResolve(false)}>
              {request.cancelLabel ?? 'Cancel'}
            </button>
          )}
          <button
            type="button"
            className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
            autoFocus
            onClick={() => onResolve(true)}
          >
            {request.confirmLabel ?? 'OK'}
          </button>
        </div>
      }
    >
      <div className="confirm-dialog__body">
        <span className="confirm-dialog__icon" aria-hidden="true">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {TONE_ICON[tone]}
          </svg>
        </span>
        <div className="confirm-dialog__message">{request.message}</div>
      </div>
    </Modal>
  );
};

/**
 * Returns `confirm` / `notify` to call from anywhere in the component, plus the
 * element to render once. Both resolve when the user answers, so they can be
 * awaited in the middle of an async flow.
 */
export function useConfirmDialog() {
  const [request, setRequest] = useState<PendingRequest | null>(null);

  const ask = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>(resolve => setRequest({ ...options, resolve })),
    []
  );

  /** Two buttons. Resolves true when the user goes ahead. */
  const confirm = useCallback(
    (options: ConfirmOptions & { confirmLabel: string }) => ask(options),
    [ask]
  );

  /** One button. Resolves when acknowledged; use where alert() was. */
  const notify = useCallback(
    (options: Omit<ConfirmOptions, 'confirmLabel' | 'cancelLabel'>) =>
      ask({ ...options, tone: options.tone ?? 'danger' }),
    [ask]
  );

  const onResolve = useCallback(
    (confirmed: boolean) => {
      setRequest(current => {
        current?.resolve(confirmed);
        return null;
      });
    },
    []
  );

  const dialog = <ConfirmDialog request={request} onResolve={onResolve} />;

  return { confirm, notify, dialog };
}

export default ConfirmDialog;
